import { useEffect, useReducer, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isLoggedInAtom, userAtom } from '@/state/loginState';

/**
 * 서버 기능 공개 상태(커리어 결제·명치사 AI)를 잠깐 기억해 두는 저장소 — docs/feature-settings-ai-cost-plan.md 6장.
 *
 * - 서버는 로그인 회원 기준(전체 공개 또는 화이트리스트)으로 답하므로, 값은 "누구로 조회했는지"(viewerKey)에 묶는다.
 *   로그인·로그아웃·계정 전환으로 viewerKey 가 바뀌면 다시 조회한다.
 * - 같은 사람의 값은 5분 동안 쓰고(TTL), 그 뒤 화면이 다시 쓰거나 5분 주기 확인 때 다시 조회한다.
 * - 앱(또는 브라우저 탭)이 앞으로 올 때(visibilitychange·Capacitor resume)는 TTL 과 상관없이 다시 조회한다.
 * - 같은 사람에 대한 조회는 한 번만 날아간다(동시에 여러 화면이 불러도).
 */

import { FEATURE_STATUS_TTL_MS, type FeatureStatusStore } from './featureStatusStore';
export { createFeatureStatusStore, FEATURE_STATUS_TTL_MS } from './featureStatusStore';
export type { FeatureStatusStore } from './featureStatusStore';

// ───────── 앱이 앞으로 올 때 ─────────

const foregroundListeners = new Set<() => void>();
let foregroundAttached = false;

const emitForeground = (): void => foregroundListeners.forEach((listener) => listener());

/** 처음 쓸 때 한 번만 붙인다 — 브라우저 탭이 다시 보일 때, 네이티브 앱이 백그라운드에서 돌아올 때 */
const attachForeground = (): void => {
    if (foregroundAttached) return;
    foregroundAttached = true;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') emitForeground();
    });
    if (Capacitor.isNativePlatform()) {
        // WebView 에 따라 visibilitychange 가 늦거나 빠질 수 있어 resume 도 받는다(겹쳐도 조회는 한 번 — 진행 중 조회를 같이 쓴다)
        void CapacitorApp.addListener('resume', emitForeground);
    }
};

/** 앱(탭)이 앞으로 올 때 부를 함수를 등록한다. 돌려준 함수로 해제 */
export const onAppForeground = (listener: () => void): (() => void) => {
    attachForeground();
    foregroundListeners.add(listener);
    return () => {
        foregroundListeners.delete(listener);
    };
};

// ───────── 화면에서 쓰기 ─────────

/** 지금 보는 사람 — 비로그인 'anon', 로그인 userNo(프로필을 받기 전이면 'pending' — 받은 뒤 다시 조회) */
export const useViewerKey = (): string => {
    const isLoggedIn: boolean = useRecoilValue(isLoggedInAtom);
    const user = useRecoilValue(userAtom);
    return !isLoggedIn ? 'anon' : user?.no ? String(user.no) : 'pending';
};

/**
 * 저장소의 값을 화면에서 쓴다 — 처음·사람이 바뀔 때·5분마다(화면이 보일 때)·앱이 앞으로 올 때 조회.
 *
 * @param active false 면 조회하지 않는다(예: 강사가 아닌 회원의 정산 조회)
 * @returns value 이 사람의 값(받기 전이면 null)
 */
export function useFeatureStatus<T>(store: FeatureStatusStore<T>, active = true): { value: T | null; viewerKey: string } {
    const viewerKey: string = useViewerKey();
    const [, rerender] = useReducer((count: number) => count + 1, 0);
    const viewerKeyRef = useRef<string>(viewerKey);
    viewerKeyRef.current = viewerKey;

    useEffect(() => store.subscribe(rerender), [store]);

    useEffect(() => {
        if (active) void store.load(viewerKey);
    }, [store, viewerKey, active]);

    useEffect(() => {
        if (!active) return undefined;
        const offForeground = onAppForeground(() => {
            void store.load(viewerKeyRef.current, true);
        });
        // 화면을 오래 띄워 둔 경우 — TTL 이 지나면 다시 조회(보이지 않는 탭에서는 하지 않고, 앞으로 올 때 한다)
        const timer: number = window.setInterval(() => {
            if (document.visibilityState === 'visible') void store.load(viewerKeyRef.current);
        }, FEATURE_STATUS_TTL_MS);
        return () => {
            offForeground();
            window.clearInterval(timer);
        };
    }, [store, active]);

    return { value: store.peek(viewerKey), viewerKey };
}
